import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "Is SAINT TMS available near me?",
    answer:
      "SAINT is available at a limited number of verified providers. Search Magnus Medical's official provider locator to find one near you. If none is nearby, many patients travel for the 5-day treatment because it is much shorter than a conventional 6-week TMS course.",
  },
  {
    question: "Why isn't SAINT available at my local TMS clinic?",
    answer:
      "SAINT requires the Magnus Neuromodulation System, fMRI targeting, and manufacturer-certified training. Most TMS clinics use conventional devices cleared for once-daily treatment. SAINT was FDA-cleared in 2022 and is still expanding to new providers.",
  },
  {
    question: "Can I get SAINT as an outpatient?",
    answer:
      "Yes. SAINT is delivered in an outpatient setting. Patients are awake during treatment, no anesthesia is used, and most can drive themselves home. Neuro Wellness delivers SAINT in a private outpatient suite in Coral Springs, Florida.",
  },
  {
    question: "What if there's no SAINT provider in my state?",
    answer:
      "Patients from states without a SAINT provider travel to one that has one. Because the treatment takes five consecutive days rather than six weeks, travel is often practical. Neuro Wellness serves traveling patients from across Florida and out of state.",
  },
];

export const pageIsSaintAvailableNearMe: LearnPageData = {
  slug: "is-saint-available-near-me",
  title: "Is SAINT® TMS Available Near Me? | Neuro Wellness",
  description:
    "SAINT® TMS is available at a limited number of verified providers. Learn how to check availability near you and why many patients travel for the 5-day treatment.",
  canonical: "/learn/is-saint-available-near-me",
  h1: "Is SAINT® TMS Available Near Me?",
  breadcrumbLabel: "Is SAINT Available Near Me?",
  crumbs: baseCrumbs("Is SAINT Available Near Me?"),
  shortAnswer:
    "SAINT® TMS is available at a limited number of verified providers listed on Magnus Medical's official locator. Because SAINT requires the Magnus Neuromodulation System and fMRI targeting, it is not available at every TMS clinic. If no verified provider is nearby, many patients travel for the five-day treatment, which is much shorter than a conventional six-week TMS course. Neuro Wellness TMS Centers of America is a verified SAINT provider in Coral Springs, Florida.",
  keyTakeaways: [
    "SAINT is available at verified providers listed on Magnus Medical's locator.",
    "It is not available at every TMS clinic.",
    "Many patients travel for the 5-day treatment.",
    "SAINT is delivered as an outpatient — no hospitalization required.",
  ],

  sections: [
    {
      heading: "How to Check Availability",
      body: (
        <>
          {P(
            "Search Magnus Medical's official provider locator by state or city. If a clinic is listed, it is a verified SAINT provider. If your area has no listing, the nearest verified provider may require travel.",
          )}
        </>
      ),
    },
    {
      heading: "Why Availability Is Limited",
      body: (
        <>
          {P(
            "SAINT requires the Magnus Neuromodulation System (FDA-cleared September 2022), resting-state fMRI, and manufacturer-certified training. Conventional TMS devices are far more widely available. Magnus Medical reported SAINT was available in 14 states as of May 2026.",
          )}
        </>
      ),
    },
    {
      heading: "Traveling for Treatment",
      body: (
        <>
          {P(
            <>
              <a
                href="/traveling-for-treatment"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                Traveling for SAINT
              </a>{" "}
              is common. The five-day schedule makes it practical for patients
              from other cities or states. Plan to arrive the day before
              treatment, stay locally for the week, and arrange follow-up with
              your home clinicians.
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Outpatient Treatment",
      body: (
        <>
          {P(
            "SAINT is delivered in an outpatient setting. Patients are awake, no anesthesia is used, and most can drive afterward. Neuro Wellness offers SAINT in a private outpatient suite in Coral Springs, Florida.",
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation: "Magnus Medical. Provider locator and availability.",
      url: "https://www.magnusmedical.com/",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/is-saint-available-near-me#webpage`,
        name: "Is SAINT® TMS Available Near Me?",
        url: `${SITE_URL}/learn/is-saint-available-near-me`,
        description:
          "How to check SAINT availability and why many patients travel for treatment.",
      }),
      orgNode,
      faqJsonld(faqs, `${SITE_URL}/learn/is-saint-available-near-me#faq`),
      breadcrumbJsonld(baseCrumbs("Is SAINT Available Near Me?")),
    ],
  },
  relatedLinks: [
    {
      title: "Find a Verified SAINT® Provider",
      href: "/find-a-verified-saint-provider",
      description: "Neuro Wellness's official listing",
    },
    {
      title: "SAINT TMS Near Me",
      href: "/learn/saint-tms-near-me",
      description: "Expanding your search radius",
    },
    {
      title: "Traveling for Treatment",
      href: "/traveling-for-treatment",
      description: "Planning your trip for SAINT",
    },
    ...sharedRelated,
  ],
};
